// Comprehensive Program Building Track - 5 Phases
export const programPhases = [
  {
    id: 'phase-1',
    order: 1,
    name: 'Program Structure & Governance',
    duration: '2-3 weeks',
    overview: 'Establish the foundation of your compliance program by defining structure, governance model, and organizational alignment.',

    checklist: [
      {
        category: 'Leadership & Governance',
        items: [
          { task: 'Define program scope and mandate', completed: false },
          { task: 'Establish compliance committee', completed: false },
          { task: 'Appoint Chief Compliance Officer (CCO)', completed: false },
          { task: 'Define reporting lines and escalation paths', completed: false },
          { task: 'Create governance charter document', completed: false }
        ]
      },
      {
        category: 'Organizational Structure',
        items: [
          { task: 'Map current organization structure', completed: false },
          { task: 'Identify key stakeholders and business units', completed: false },
          { task: 'Define compliance roles and responsibilities', completed: false },
          { task: 'Allocate budget and resources', completed: false },
          { task: 'Create staffing plan', completed: false }
        ]
      },
      {
        category: 'Documentation',
        items: [
          { task: 'Document program objectives', completed: false },
          { task: 'Create organizational chart', completed: false },
          { task: 'Define decision-making authority matrix', completed: false },
          { task: 'Establish communication protocols', completed: false }
        ]
      }
    ],

    implementationGuide: {
      sections: [
        {
          title: 'Step 1: Define Program Scope',
          content: 'Determine which business areas, products, and geographies fall under the compliance program. Consider regulatory requirements, organizational structure, and risk profile.',
          duration: '3-5 days'
        },
        {
          title: 'Step 2: Establish Leadership',
          content: 'Identify executive sponsors and appoint a Chief Compliance Officer with adequate authority, independence, and resources to execute the program effectively.',
          duration: '1 week'
        },
        {
          title: 'Step 3: Create Governance Framework',
          content: 'Develop governance structures including compliance committee, board oversight mechanisms, and escalation procedures.',
          duration: '1 week'
        },
        {
          title: 'Step 4: Map Organizational Landscape',
          content: 'Analyze your organization\'s structure, identify key business units, and determine how compliance will integrate across functions.',
          duration: '3-5 days'
        },
        {
          title: 'Step 5: Allocate Resources',
          content: 'Determine staffing requirements, budget allocation, and technology needs to support the program.',
          duration: '1 week'
        }
      ]
    },

    templates: [
      {
        name: 'Program Charter Document',
        description: 'Formal document outlining program authority, scope, objectives, and governance structure',
        content: `
COMPLIANCE PROGRAM CHARTER

1. PURPOSE
Define the compliance program\'s authority, scope, and organizational structure.

2. AUTHORITY & GOVERNANCE
- Program Sponsor: [Board/CEO]
- Chief Compliance Officer: [Name/Title]
- Reporting Line: [Direct to Board/CEO]
- Decision Authority: [Define decision rights]

3. PROGRAM SCOPE
- Business Areas: [List applicable areas]
- Geographies: [List applicable regions]
- Regulatory Requirements: [List key regulations]

4. KEY OBJECTIVES
- Ensure regulatory compliance across all operations
- Establish culture of compliance and integrity
- Implement effective controls and monitoring
- Report on compliance status to board

5. ROLES & RESPONSIBILITIES
[Define key roles and accountability]

6. ESCALATION & REPORTING
[Define escalation procedures and reporting cadence]
        `
      },
      {
        name: 'Organizational Chart Template',
        description: 'Template for mapping compliance team structure and reporting relationships',
        content: `
COMPLIANCE ORGANIZATION STRUCTURE

Board of Directors/Audit Committee
    |
Chief Compliance Officer
    |
    ├─ Head of Risk & Controls
    ├─ Head of Policy & Procedures
    ├─ Head of Monitoring & Testing
    ├─ Head of Training & Communications
    └─ Compliance Coordinator

[Customize based on organization size and complexity]
        `
      },
      {
        name: 'Stakeholder Engagement Plan',
        description: 'Plan for identifying and engaging key stakeholders in program development',
        content: `
STAKEHOLDER ENGAGEMENT PLAN

Stakeholder Groups:
1. Executive Leadership - Communication: Quarterly reviews
2. Business Unit Heads - Communication: Monthly updates
3. Front-Line Staff - Communication: Training & awareness
4. Board/Audit Committee - Communication: Board meetings
5. External Regulators - Communication: As required

Engagement Strategy:
- Establish compliance committee
- Create business unit liaisons
- Schedule regular steering committee meetings
- Plan awareness campaigns
        `
      }
    ],

    trainingModules: [
      {
        title: 'Compliance Program Fundamentals',
        duration: '45 minutes',
        topics: ['Program objectives', 'Governance structures', 'Key roles']
      },
      {
        title: 'Leadership and Oversight',
        duration: '30 minutes',
        topics: ['Board oversight', 'Executive accountability', 'Escalation procedures']
      },
      {
        title: 'Organizational Alignment',
        duration: '40 minutes',
        topics: ['Stakeholder engagement', 'Change management', 'Cross-functional collaboration']
      }
    ]
  },

  {
    id: 'phase-2',
    order: 2,
    name: 'Risk Assessment & Control Design',
    duration: '4-6 weeks',
    overview: 'Conduct comprehensive risk assessment and design control framework to mitigate identified risks.',

    checklist: [
      {
        category: 'Risk Assessment',
        items: [
          { task: 'Identify regulatory requirements and obligations', completed: false },
          { task: 'Map business processes and control points', completed: false },
          { task: 'Conduct risk assessment workshops', completed: false },
          { task: 'Develop risk scoring methodology', completed: false },
          { task: 'Prioritize risks by inherent and residual risk', completed: false }
        ]
      },
      {
        category: 'Control Design',
        items: [
          { task: 'Define control objectives for each risk', completed: false },
          { task: 'Design preventive controls', completed: false },
          { task: 'Design detective controls', completed: false },
          { task: 'Document control procedures', completed: false },
          { task: 'Assign control ownership', completed: false }
        ]
      },
      {
        category: 'Control Framework',
        items: [
          { task: 'Create control matrix', completed: false },
          { task: 'Define control testing methodology', completed: false },
          { task: 'Establish KRI and KPIs', completed: false },
          { task: 'Create control effectiveness dashboard', completed: false }
        ]
      }
    ],

    implementationGuide: {
      sections: [
        {
          title: 'Step 1: Regulatory Mapping',
          content: 'Document all applicable regulations, laws, and requirements that impact your organization.',
          duration: '1-2 weeks'
        },
        {
          title: 'Step 2: Process Mapping',
          content: 'Map critical business processes and identify control points where risks can be mitigated.',
          duration: '2 weeks'
        },
        {
          title: 'Step 3: Risk Assessment',
          content: 'Conduct structured risk assessment workshops to identify, analyze, and prioritize compliance risks.',
          duration: '2 weeks'
        },
        {
          title: 'Step 4: Control Design',
          content: 'Design compensating, preventive, and detective controls to mitigate identified risks.',
          duration: '2 weeks'
        }
      ]
    },

    templates: [
      {
        name: 'Risk Assessment Template',
        description: 'Structured template for conducting risk assessment',
        content: `
RISK ASSESSMENT TEMPLATE

Risk ID | Regulatory Area | Process | Risk Description | Inherent Risk | Residual Risk | Control Owner
--------|-----------------|---------|------------------|----------------|----------------|---------------
RA-001  | AML/CFT         | Onboarding | Customer not properly screened | High | Medium | Compliance
RA-002  | Sanctions       | Transactions | Transaction not screened | High | Medium | Operations

Risk Rating Scale:
- High: Potential for significant regulatory impact
- Medium: Moderate regulatory impact
- Low: Minimal regulatory impact
        `
      },
      {
        name: 'Control Matrix Template',
        description: 'Document mapping risks to controls and control effectiveness',
        content: `
CONTROL MATRIX

Risk | Control ID | Control Description | Type | Frequency | Effectiveness | Owner
-----|------------|---------------------|------|-----------|---------------|---------
RA-001 | C-001 | Customer screening at onboarding | Preventive | Continuous | 95% | Compliance
RA-001 | C-002 | Quarterly refresh of customer data | Detective | Quarterly | 90% | Compliance
RA-002 | C-003 | Real-time transaction screening | Preventive | Continuous | 98% | Operations

Control Types: Preventive, Detective, Corrective
        `
      }
    ],

    trainingModules: [
      {
        title: 'Risk Assessment Methodologies',
        duration: '60 minutes',
        topics: ['Risk identification', 'Risk scoring', 'Prioritization techniques']
      },
      {
        title: 'Control Design Principles',
        duration: '45 minutes',
        topics: ['Control objectives', 'Preventive vs. Detective', 'Control effectiveness']
      }
    ]
  },

  {
    id: 'phase-3',
    order: 3,
    name: 'Policy & Procedure Development',
    duration: '4-5 weeks',
    overview: 'Develop comprehensive policies, procedures, and standards that govern compliance activities.',

    checklist: [
      {
        category: 'Policy Development',
        items: [
          { task: 'Identify policies needed based on risks', completed: false },
          { task: 'Draft core compliance policies', completed: false },
          { task: 'Establish policy governance and approval process', completed: false },
          { task: 'Implement version control and change management', completed: false },
          { task: 'Define policy review and update schedule', completed: false }
        ]
      },
      {
        category: 'Procedures & Standards',
        items: [
          { task: 'Document standard procedures for key processes', completed: false },
          { task: 'Create step-by-step work instructions', completed: false },
          { task: 'Define quality standards and acceptance criteria', completed: false },
          { task: 'Establish escalation procedures', completed: false }
        ]
      },
      {
        category: 'Communication & Training',
        items: [
          { task: 'Create policy communication plan', completed: false },
          { task: 'Develop training materials', completed: false },
          { task: 'Document acknowledgment procedures', completed: false },
          { task: 'Plan ongoing awareness communications', completed: false }
        ]
      }
    ],

    implementationGuide: {
      sections: [
        {
          title: 'Step 1: Policy Framework',
          content: 'Establish policy governance structure, including approval authority, version control, and update procedures.',
          duration: '1 week'
        },
        {
          title: 'Step 2: Core Policy Development',
          content: 'Develop core compliance policies addressing AML/CFT, Sanctions, KYC, Conflicts of Interest, etc.',
          duration: '2 weeks'
        },
        {
          title: 'Step 3: Procedure Documentation',
          content: 'Create detailed procedures and work instructions for key compliance processes.',
          duration: '1-2 weeks'
        },
        {
          title: 'Step 4: Dissemination & Training',
          content: 'Communicate policies and procedures to all staff and provide training on requirements.',
          duration: '1 week'
        }
      ]
    },

    templates: [
      {
        name: 'Policy Template',
        description: 'Standard template for compliance policy documents',
        content: `
COMPLIANCE POLICY TEMPLATE

1. POLICY NAME: [Policy Title]

2. EFFECTIVE DATE: [Date]

3. PURPOSE
[Brief description of policy purpose and scope]

4. SCOPE
Who is covered by this policy: [Define]
Geographic coverage: [Define]

5. POLICY STATEMENT
[Detailed policy requirements]

6. ROLES & RESPONSIBILITIES
- Role 1: [Responsibility]
- Role 2: [Responsibility]

7. PROCEDURES
[Step-by-step procedures for implementation]

8. MONITORING & TESTING
How compliance will be monitored: [Define]
Testing frequency: [Define]

9. ESCALATION & CONSEQUENCES
Non-compliance consequences: [Define]
        `
      },
      {
        name: 'Procedure Documentation Template',
        description: 'Template for documenting specific procedures',
        content: `
PROCEDURE: [Procedure Name]

Process Owner: [Name/Department]
Version: 1.0
Last Updated: [Date]

1. PURPOSE
[Why this procedure exists]

2. SCOPE
[Who performs it, when]

3. STEP-BY-STEP INSTRUCTIONS
Step 1: [Description]
Step 2: [Description]
Step 3: [Description]

4. CONTROLS
Key controls in this process: [List]

5. ESCALATION PATHS
If issue occurs: [Escalation path]

6. SUPPORTING TEMPLATES/FORMS
[List forms used]
        `
      }
    ],

    trainingModules: [
      {
        title: 'Policy Development & Management',
        duration: '40 minutes',
        topics: ['Policy governance', 'Change management', 'Version control']
      },
      {
        title: 'Procedure Documentation Best Practices',
        duration: '35 minutes',
        topics: ['Process documentation', 'Standard procedures', 'Work instructions']
      }
    ]
  },

  {
    id: 'phase-4',
    order: 4,
    name: 'Implementation & Deployment',
    duration: '6-8 weeks',
    overview: 'Deploy controls and procedures across the organization with comprehensive change management.',

    checklist: [
      {
        category: 'System Implementation',
        items: [
          { task: 'Select and configure compliance systems', completed: false },
          { task: 'Integrate with existing systems', completed: false },
          { task: 'Configure workflows and automated controls', completed: false },
          { task: 'Set up reporting and dashboards', completed: false },
          { task: 'Conduct system testing and validation', completed: false }
        ]
      },
      {
        category: 'Process Implementation',
        items: [
          { task: 'Roll out procedures to all business units', completed: false },
          { task: 'Establish control ownership and accountability', completed: false },
          { task: 'Train all staff on new procedures', completed: false },
          { task: 'Establish monitoring routines', completed: false },
          { task: 'Create escalation procedures', completed: false }
        ]
      },
      {
        category: 'Change Management',
        items: [
          { task: 'Develop change management plan', completed: false },
          { task: 'Create communications strategy', completed: false },
          { task: 'Provide training and support', completed: false },
          { task: 'Monitor adoption and address issues', completed: false },
          { task: 'Conduct post-implementation review', completed: false }
        ]
      }
    ],

    implementationGuide: {
      sections: [
        {
          title: 'Step 1: Technology Deployment',
          content: 'Select appropriate compliance technology and configure systems to support control execution.',
          duration: '2-3 weeks'
        },
        {
          title: 'Step 2: Staff Training',
          content: 'Conduct comprehensive training to ensure all staff understand new procedures and systems.',
          duration: '1-2 weeks'
        },
        {
          title: 'Step 3: Pilot Deployment',
          content: 'Implement controls in a pilot business unit to validate procedures and identify issues.',
          duration: '2 weeks'
        },
        {
          title: 'Step 4: Enterprise Rollout',
          content: 'Deploy controls across all business units with ongoing support and monitoring.',
          duration: '2-3 weeks'
        }
      ]
    },

    templates: [
      {
        name: 'Implementation Plan',
        description: 'Detailed plan for rolling out controls and procedures',
        content: `
IMPLEMENTATION PLAN

Project Name: [Name]
Timeline: [Dates]
Budget: [Amount]

1. PROJECT PHASES
Phase 1: Technology Setup (Weeks 1-3)
Phase 2: Training (Weeks 2-4)
Phase 3: Pilot (Weeks 4-6)
Phase 4: Full Rollout (Weeks 6-8)

2. RESOURCES REQUIRED
- Project Manager: [Name]
- Technical Resources: [Count/Skills]
- Training Resources: [Count]
- Budget: [Amount]

3. TIMELINE & MILESTONES
[Gantt chart or timeline]

4. RISK & MITIGATION
Risk | Probability | Impact | Mitigation
-----|-------------|--------|----------

5. SUCCESS METRICS
- Staff trained: 100%
- Controls operational: 100%
- System uptime: 99.5%
        `
      },
      {
        name: 'Training Needs Assessment',
        description: 'Assess training needs by role and create training plan',
        content: `
TRAINING NEEDS ASSESSMENT

Role | Training Required | Duration | Delivery Method | Schedule
-----|------------------|----------|-----------------|----------
Front-line Staff | Procedures | 2 hours | Classroom | Week 1-2
Managers | Oversight & Escalation | 1.5 hours | Virtual | Week 1
Compliance Team | Detailed System Usage | 8 hours | Hands-on | Ongoing

Training Materials:
- Job aids
- Video tutorials
- Quick reference guides
- FAQs
        `
      }
    ],

    trainingModules: [
      {
        title: 'Change Management Excellence',
        duration: '50 minutes',
        topics: ['Stakeholder engagement', 'Communication strategies', 'Resistance management']
      },
      {
        title: 'System Implementation & Deployment',
        duration: '60 minutes',
        topics: ['System configuration', 'Testing', 'Go-live management']
      },
      {
        title: 'Training Program Development',
        duration: '45 minutes',
        topics: ['Training needs assessment', 'Training design', 'Delivery methods']
      }
    ]
  },

  {
    id: 'phase-5',
    order: 5,
    name: 'Monitoring, Testing & Continuous Improvement',
    duration: 'Ongoing',
    overview: 'Establish monitoring and testing frameworks to ensure ongoing program effectiveness and continuous improvement.',

    checklist: [
      {
        category: 'Monitoring & Surveillance',
        items: [
          { task: 'Establish daily/weekly monitoring procedures', completed: false },
          { task: 'Configure automated controls and alerts', completed: false },
          { task: 'Create monitoring dashboard and KPIs', completed: false },
          { task: 'Conduct trend analysis of control issues', completed: false },
          { task: 'Establish escalation procedures for exceptions', completed: false }
        ]
      },
      {
        category: 'Testing & Compliance Validation',
        items: [
          { task: 'Develop testing methodology and protocols', completed: false },
          { task: 'Create testing schedule and prioritization', completed: false },
          { task: 'Conduct control effectiveness testing', completed: false },
          { task: 'Document testing results and findings', completed: false },
          { task: 'Track remediation of control issues', completed: false }
        ]
      },
      {
        category: 'Reporting & Improvement',
        items: [
          { task: 'Establish reporting cadence and audience', completed: false },
          { task: 'Create compliance dashboard for management', completed: false },
          { task: 'Develop issue tracking and remediation system', completed: false },
          { task: 'Conduct periodic program reviews', completed: false },
          { task: 'Implement continuous improvement processes', completed: false }
        ]
      }
    ],

    implementationGuide: {
      sections: [
        {
          title: 'Step 1: Monitoring Framework',
          content: 'Establish daily, weekly, and monthly monitoring procedures to detect control issues.',
          duration: 'Ongoing'
        },
        {
          title: 'Step 2: Testing Program',
          content: 'Develop comprehensive testing methodology to validate control effectiveness.',
          duration: 'Ongoing'
        },
        {
          title: 'Step 3: Reporting Structure',
          content: 'Create reporting structure for board, management, and stakeholders.',
          duration: 'Ongoing'
        },
        {
          title: 'Step 4: Continuous Improvement',
          content: 'Establish processes to continuously improve program effectiveness based on testing results.',
          duration: 'Ongoing'
        }
      ]
    },

    templates: [
      {
        name: 'Control Testing Protocol',
        description: 'Protocol for testing control effectiveness',
        content: `
CONTROL TESTING PROTOCOL

Control ID: C-001
Control Name: Customer screening at onboarding
Test Frequency: Monthly
Test Method: Sample testing of 50+ new customers

1. TEST OBJECTIVE
Verify that all new customers are screened against watchlists.

2. TEST POPULATION
Sample: 50 new customer accounts opened in period

3. TEST PROCEDURE
Step 1: Pull sample of customer accounts
Step 2: Verify screening was performed
Step 3: Review watchlist results
Step 4: Document findings

4. ACCEPTANCE CRITERIA
- 100% of customers screened
- Results documented
- Exceptions escalated

5. TESTING RESULTS
[Document findings]

6. REMEDIATION
[If any issues found]
        `
      },
      {
        name: 'Monitoring & Reporting Dashboard',
        description: 'Template for monitoring dashboard metrics',
        content: `
MONITORING DASHBOARD

Key Compliance Indicators:
1. Control Effectiveness Rate: [%]
2. Exception Rate by Control: [%]
3. Remediation Timeliness: [Days average]
4. Training Completion: [%]
5. System Uptime: [%]
6. Regulatory Violations: [Count]

Monitoring Frequency:
- Daily: Real-time control exceptions
- Weekly: Performance metrics review
- Monthly: Detailed compliance report
- Quarterly: Program effectiveness review

Reporting Audience:
- Board/Audit Committee: Quarterly
- Executive Management: Monthly
- Compliance Team: Weekly/Daily
        `
      }
    ],

    trainingModules: [
      {
        title: 'Monitoring & Surveillance Best Practices',
        duration: '50 minutes',
        topics: ['Automated monitoring', 'KPI design', 'Exception management']
      },
      {
        title: 'Control Testing & Validation',
        duration: '55 minutes',
        topics: ['Testing methodologies', 'Sample selection', 'Documentation']
      },
      {
        title: 'Compliance Reporting & Analytics',
        duration: '45 minutes',
        topics: ['Dashboards', 'Reporting cadence', 'Data analysis']
      }
    ]
  }
];

export default programPhases;
